import { db } from './firebase.js';
import { doc, getDoc, runTransaction, serverTimestamp, setDoc } from 'firebase/firestore';

export type ProfileVisibility = 'private' | 'public' | 'link';

export type UserProfile = {
  handle?: string;
  displayName?: string;
  photoURL?: string;
  bio?: string;
  links?: Partial<Record<'cardmarket'|'vinted'|'olx'|'shop'|'website'|'twitter'|'instagram', string>>;
  visibility?: ProfileVisibility;
  featuredKeys?: string[];
  stats?: { owned?: number; wanted?: number; setsCompleted?: number };
  createdAt?: any;
  updatedAt?: any;
};

export function normalizeHandle(input: string): string {
  return String(input || '')
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);
}

export async function getProfile(uid: string): Promise<UserProfile | null> {
  if (!db || !uid) return null;
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() as UserProfile) : null;
}

export async function saveProfile(uid: string, data: Partial<UserProfile>): Promise<void> {
  if (!db || !uid) return;
  const ref = doc(db, 'users', uid);
  await setDoc(ref, { ...data, updatedAt: serverTimestamp(), createdAt: serverTimestamp() }, { merge: true });
}

export async function getUidByHandle(handle: string): Promise<string | null> {
  if (!db) return null;
  const slug = normalizeHandle(handle);
  if (!slug) return null;
  const ref = doc(db, 'handles', slug);
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() as any).uid || null : null;
}

export async function checkHandleAvailability(handle: string, currentUid?: string): Promise<{ available: boolean; reason?: string }> {
  if (!db) return { available: false, reason: 'no-db' };
  const normalized = normalizeHandle(handle);
  if (!normalized) return { available: false, reason: 'invalid' };
  if (normalized.length < 3) return { available: false, reason: 'too-short' };
  if (normalized.length > 24) return { available: false, reason: 'too-long' };
  
  const ref = doc(db, 'handles', normalized);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) return { available: true };
  
  const existingUid = (snap.data() as any).uid;
  if (currentUid && existingUid === currentUid) return { available: true };
  
  return { available: false, reason: 'taken' };
}

export async function claimHandle(uid: string, desired: string): Promise<{ ok: true } | { ok: false; reason: string }>{
  if (!db || !uid) return { ok: false, reason: 'no-db' };
  const handle = normalizeHandle(desired);
  if (!handle) return { ok: false, reason: 'invalid' };
  const handleRef = doc(db, 'handles', handle);
  const userRef = doc(db, 'users', uid);
  try {
    await runTransaction(db, async (tx) => {
      const handleDoc = await tx.get(handleRef);
      if (handleDoc.exists() && (handleDoc.data() as any).uid !== uid) {
        throw new Error('taken');
      }
      tx.set(handleRef, { uid, updatedAt: serverTimestamp() }, { merge: true });
      tx.set(userRef, { handle, updatedAt: serverTimestamp(), createdAt: serverTimestamp() }, { merge: true });
    });
    return { ok: true };
  } catch (e: any) {
    if (String(e?.message).includes('taken')) return { ok: false, reason: 'taken' };
    return { ok: false, reason: 'error' };
  }
}

export async function ensureUserProfile(uid: string, init: Partial<UserProfile> = {}): Promise<void> {
  if (!db || !uid) return;
  const ref = doc(db, 'users', uid);
  const base: Partial<UserProfile> = {
    visibility: init.visibility || 'public',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    stats: { owned: 0, wanted: 0, setsCompleted: 0 },
  };
  if (typeof init.displayName === 'string' && init.displayName) {
    base.displayName = init.displayName;
  }
  if (typeof init.photoURL === 'string' && init.photoURL) {
    base.photoURL = init.photoURL;
  }
  // Write with merge without reading first to avoid rules denying the read on non-existent doc
  await setDoc(ref, base, { merge: true });
}


